import React, { useState, useEffect, useMemo, useCallback } from 'react';

const Hero = ({ scrollToSection }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [typingIndex, setTypingIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(true);

  // ✅ Menggunakan useMemo untuk data statis
  const slides = useMemo(() => [
    { 
      title: "HOST MOMOLIVE", 
      description: "Bergabunglah dengan agensi kami dan raih kesempatan menjadi host terbaik!",
      badge: "✨ OPEN RECRUITMENT ✨",
      icon: "🎤"
    },
    { 
      title: "MULTI TALENT", 
      description: "Kembangkan bakatmu dan jadi bintang dengan dukungan penuh dari kami!",
      badge: "🌟 JOIN NOW 🌟",
      icon: "⭐"
    },
    { 
      title: "CREATOR BERKARYA", 
      description: "Tunjukkan kreativitasmu dan raih penghasilan menarik setiap bulannya!",
      badge: "💫 LIMITED SLOT 💫",
      icon: "💎"
    },
    { 
      title: "INFLUENCER HEBAT", 
      description: "Jadi influencer ternama dengan jutaan penggemar dan pendapatan melimpah!",
      badge: "🚀 GROW TOGETHER 🚀",
      icon: "🚀"
    }
  ], []); // ✅ Dependency array kosong karena data statis

  // ✅ Memisahkan logika typing ke dalam useEffect terpisah
  useEffect(() => {
    const currentFullText = slides[currentSlide].title;
    
    if (isTyping) {
      if (typingIndex < currentFullText.length) {
        const timeout = setTimeout(() => {
          setDisplayText(prev => prev + currentFullText[typingIndex]);
          setTypingIndex(prev => prev + 1);
        }, 80);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => {
          setIsTyping(false);
        }, 3000);
        return () => clearTimeout(timeout);
      }
    } else {
      if (typingIndex > 0) {
        const timeout = setTimeout(() => {
          setDisplayText(prev => prev.slice(0, -1));
          setTypingIndex(prev => prev - 1);
        }, 50);
        return () => clearTimeout(timeout);
      } else {
        setIsTyping(true);
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }
    }
  }, [typingIndex, isTyping, currentSlide, slides]); // ✅ Semua dependencies sudah termasuk

  // ✅ Reset typing state ketika slide berubah
  useEffect(() => {
    setDisplayText('');
    setTypingIndex(0);
    setIsTyping(true);
  }, [currentSlide]);

  // ✅ Blinking cursor effect
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);
    return () => clearInterval(cursorInterval);
  }, []); // ✅ Tidak ada dependencies

  // ✅ Auto slide dengan interval 10 detik
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 10000);
    return () => clearInterval(slideInterval);
  }, [slides.length]); // ✅ Menggunakan slides.length, bukan slides

  // ✅ Menggunakan useCallback untuk function handler yang stabil
  const handleIndicatorClick = useCallback((index) => {
    setCurrentSlide(index);
    setDisplayText('');
    setTypingIndex(0);
    setIsTyping(true);
  }, []); // ✅ Tidak ada dependencies karena hanya menggunakan setState

  // ✅ scrollToSection sudah distabilkan dari parent component (opsional)
  // Jika parent belum menggunakan useCallback, bisa juga dibungkus di sini
  
  return (
    <section className="hero" id="hero">
      <div className="hero-container">
        <div className="hero-content">
          <div className="hero-badge-wrapper">
            <div className="hero-badge">
              <span className="badge-icon">{slides[currentSlide].icon}</span>
              <span className="badge-text">{slides[currentSlide].badge}</span>
            </div>
          </div>

          <div className="hero-title-wrapper">
            <h1 className="hero-title">
              <span className="hero-title-prefix">Bergabung Menjadi</span>
              <div className="hero-title-animated">
                <span className="hero-title-main">
                  {displayText}
                  <span className={`hero-cursor ${showCursor ? 'visible' : 'hidden'}`}>|</span>
                </span>
              </div>
            </h1>
          </div>

          <div className="hero-description-wrapper">
            <p className="hero-description" key={currentSlide}>
              {slides[currentSlide].description}
            </p>
          </div>

          <div className="hero-stats">
            <div className="hero-stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-number">500+</div>
              <div className="stat-label">Host Aktif</div>
            </div>
            <div className="hero-stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-number">Rp 10M+</div>
              <div className="stat-label">Total Bonus</div>
            </div>
            <div className="hero-stat-card">
              <div className="stat-icon">😊</div>
              <div className="stat-number">99%</div>
              <div className="stat-label">Kepuasan Host</div>
            </div>
          </div>

          <div className="hero-buttons">
            <button 
              className="hero-btn hero-btn-primary pulse-effect"
              onClick={() => scrollToSection('contact')}
            >
              <i className="fas fa-gem"></i>
              Daftar Sekarang
              <i className="fas fa-arrow-right"></i>
            </button>
            <button 
              className="hero-btn hero-btn-secondary"
              onClick={() => scrollToSection('levels')}
            >
              <i className="fas fa-crown"></i>
              Lihat Levels
            </button>
          </div>

          <div className="hero-slide-indicators">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`hero-indicator ${index === currentSlide ? 'active' : ''}`}
                onClick={() => handleIndicatorClick(index)} // ✅ Menggunakan stable handler
              />
            ))}
          </div>

          <div className="hero-scroll-indicator">
            <span className="scroll-text">Scroll untuk informasi lebih</span>
            <div className="scroll-mouse">
              <div className="scroll-wheel"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="hero-background">
        <div className="hero-glow hero-glow-1"></div>
        <div className="hero-glow hero-glow-2"></div>
        <div className="hero-glow hero-glow-3"></div>
        
        <div className="particles">
          <div className="particle particle-1">✨</div>
          <div className="particle particle-2">⭐</div>
          <div className="particle particle-3">💎</div>
          <div className="particle particle-4">🎤</div>
          <div className="particle particle-5">🌟</div>
          <div className="particle particle-6">💫</div>
          <div className="particle particle-7">🎵</div>
          <div className="particle particle-8">🔥</div>
        </div>

        <div className="hero-grid"></div>
      </div>
    </section>
  );
};

export default Hero;
